/* eslint-disable no-console */
import { createClient, processLock } from '@supabase/supabase-js';

// Resolve Supabase credentials from the most common env prefixes used across
// the repo (CRA/Next). Falls back to placeholders so tests don't crash if the
// values are missing.
const supabaseUrl =
  process.env.REACT_APP_SUPABASE_URL ||
  process.env.NEXT_PUBLIC_SUPABASE_URL ||
  process.env.VITE_SUPABASE_URL;

const supabaseAnonKey =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ||
  process.env.VITE_SUPABASE_ANON_KEY;

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase credentials missing! Check your .env file or Vercel project settings.');
}

// Use fallback values if env vars are missing to prevent app crash
// This allows the app to load and show proper error messages instead of a blank screen
const defaultUrl = 'https://placeholder.supabase.co';
const defaultKey = 'placeholder';

// Exported for places that need the base URL (e.g., calling edge functions)
export const SUPABASE_URL = supabaseUrl || defaultUrl;

const browserStorage = {
  getItem: (key) => {
    try {
      // 1. Try to get from Cookie (Cross-subdomain)
      const name = key + '=';
      const decodedCookie = decodeURIComponent(document.cookie);
      const ca = decodedCookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') {
          c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
          const raw = c.substring(name.length, c.length);
          try {
            return decodeURIComponent(raw);
          } catch {
            return raw;
          }
        }
      }
      // 2. Fallback to localStorage (Dev / Migration)
      return localStorage.getItem(key);
    } catch (error) {
      console.warn('Failed to get session:', error);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      const hostname = window.location.hostname;
      const isProd = hostname.endsWith('table-tap.in');

      if (isProd) {
        // Set cross-subdomain cookie
        const domain = 'table-tap.in';
        const maxAge = 60 * 60 * 24 * 365; // 1 year
        document.cookie = `${key}=${encodeURIComponent(value)}; domain=.${domain}; path=/; max-age=${maxAge}; SameSite=Lax; Secure`;
      }

      // Always mirror locally so a single-subdomain reload still finds the session
      // if the shared cookie is rejected, truncated, or not yet readable.
      localStorage.setItem(key, value);
    } catch (error) {
      console.warn('Failed to set session:', error);
    }
  },
  removeItem: (key) => {
    try {
      const hostname = window.location.hostname;
      const isProd = hostname.endsWith('table-tap.in');

      if (isProd) {
        const domain = 'table-tap.in';
        document.cookie = `${key}=; domain=.${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; Secure`;
      }
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Failed to remove session:', error);
    }
  },
};

const storageAdapter = isBrowser
  ? browserStorage
  : {
      getItem: () => null,
      setItem: () => {},
      removeItem: () => {},
    };

const runWithoutLock = async (_name, _acquireTimeout, fn) => fn();

const hostname = isBrowser ? window.location.hostname : '';
// Web Locks / processLock often deadlock in dev (React Strict Mode + parallel
// auth calls), leaving every query queued behind a stuck token refresh. The
// lock only matters on the real production domain (cross-subdomain session
// cookie), so use it there and nowhere else — localhost, 127.0.0.1, LAN IPs,
// and preview hosts all run lock-free.
const isProdHost = hostname.endsWith('table-tap.in');
const authLock = isBrowser ? (isProdHost ? processLock : runWithoutLock) : undefined;

const supabaseClient = createClient(supabaseUrl || defaultUrl, supabaseAnonKey || defaultKey, {
  auth: {
    autoRefreshToken: isBrowser,
    persistSession: isBrowser,
    detectSessionInUrl: true,
    lock: authLock,
    lockAcquireTimeout: 20000,
    // Use default localStorage in dev to avoid stale cookies from the old
    // supabase.co project being returned and causing token refresh AbortErrors.
    // Cookie adapter is only needed on production (cross-subdomain sharing).
    storage:
      isBrowser && window.location.hostname.endsWith('table-tap.in') ? storageAdapter : undefined, // undefined = SDK uses its own localStorage
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

export const supabase = supabaseClient;

/** Use in effects/chunks where top-level supabase may not be in scope yet. */
export function getSupabase() {
  return supabaseClient;
}
